import React, { useEffect, useRef, useState } from 'react';
import { useTranslation, SUPPORTED_LANGUAGES } from '../i18n';

/**
 * LanguageSwitcher — kleine dropdown om tussen NL en EN te schakelen.
 *
 * Props:
 * - onPersist(language): optioneel. Wordt aangeroepen na een taalwissel zodat
 *   de caller de voorkeur kan syncen naar de backend. De switcher wijzigt
 *   altijd meteen de UI (optimistic) en logt eventuele backend-fouten stil —
 *   een gefaalde persist mag geen UX-blocker zijn.
 * - className: extra classes voor de wrapper.
 * - variant: 'inline' (default) toont een compact `select`; 'labeled' toont
 *   ook het "Taal" label ervoor.
 */
// Native display-naam als extra hint zodat een user in taal X toch de andere
// taal herkent (bv. "English (Engels)").
const NATIVE_LABEL = {
  nl: 'Nederlands',
  en: 'English',
};

const SHORT_LABEL = {
  nl: 'NL',
  en: 'EN',
};

const FLAG_CODE = {
  nl: 'nl',
  en: 'gb',
};

function FlagIcon({ code, className = '' }) {
  if (code === 'nl') {
    return (
      <svg viewBox="0 0 24 16" className={className} aria-hidden="true" focusable="false">
        <rect width="24" height="16" fill="#21468b" />
        <rect width="24" height="10.66" y="0" fill="#ffffff" />
        <rect width="24" height="5.33" y="0" fill="#ae1c28" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 16" className={className} aria-hidden="true" focusable="false">
      <rect width="24" height="16" fill="#012169" />
      <path d="M0 0 L24 16 M24 0 L0 16" stroke="#ffffff" strokeWidth="4" />
      <path d="M0 0 L24 16 M24 0 L0 16" stroke="#c8102e" strokeWidth="2" />
      <rect x="10" y="0" width="4" height="16" fill="#ffffff" />
      <rect x="0" y="6" width="24" height="4" fill="#ffffff" />
      <rect x="10.8" y="0" width="2.4" height="16" fill="#c8102e" />
      <rect x="0" y="6.8" width="24" height="2.4" fill="#c8102e" />
    </svg>
  );
}

export function LanguageSwitcher({ onPersist, className = '', variant = 'inline' }) {
  const { language, setLanguage, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const publicMenuRef = useRef(null);

  const handleChange = (event) => {
    const next = event.target.value;
    if (next === language) return;
    setLanguage(next);
    if (typeof onPersist === 'function') {
      try {
        const result = onPersist(next);
        if (result && typeof result.catch === 'function') {
          // eslint-disable-next-line no-console
          result.catch((err) => console.warn('[LanguageSwitcher] persist failed', err));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[LanguageSwitcher] persist threw', err);
      }
    }
  };

  const selectClassName = {
    inline: 'min-h-[42px] appearance-none rounded-xl border-2 border-[#151827]/35 bg-white px-10 py-2 pr-8 text-sm font-semibold text-[#151827] shadow-[3px_3px_0_rgba(21,24,39,0.18)] transition focus:border-[#151827] focus:outline-none',
    labeled: 'min-h-[44px] appearance-none rounded-xl border-2 border-[#151827]/35 bg-white px-10 py-2 pr-8 text-sm font-semibold text-[#151827] shadow-[3px_3px_0_rgba(21,24,39,0.18)] transition focus:border-[#151827] focus:outline-none',
  };

  useEffect(() => {
    if (variant !== 'public' || !isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (publicMenuRef.current && !publicMenuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, variant]);

  const select = (
    <select
      aria-label={t('common.language')}
      value={language}
      onChange={handleChange}
      className={selectClassName[variant] || selectClassName.inline}
    >
      {SUPPORTED_LANGUAGES.map((lang) => (
        <option key={lang} value={lang}>
          {NATIVE_LABEL[lang] || lang.toUpperCase()}
        </option>
      ))}
    </select>
  );

  const currentFlagCode = FLAG_CODE[language] || 'gb';

  const renderNativeSelectShell = (extraClass = '') => (
    <div className={`relative inline-flex items-center ${extraClass}`}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
        <FlagIcon code={currentFlagCode} className="h-3.5 w-5 rounded-[2px] border border-[#151827]/25" />
      </span>
      {select}
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-[#151827]/65">▾</span>
    </div>
  );

  if (variant === 'public') {
    const currentShort = SHORT_LABEL[language] || language.toUpperCase();

    return (
      <div ref={publicMenuRef} className={`relative inline-flex flex-col items-end ${className}`}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={t('common.language')}
          onClick={() => setIsOpen((open) => !open)}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 border-[#151827] bg-white/95 py-2 pl-4 pr-4 text-sm font-black uppercase tracking-[0.14em] text-[#151827] shadow-[6px_6px_0_#151827] backdrop-blur transition hover:bg-[#fffdf8] focus:outline-none"
        >
          <FlagIcon code={currentFlagCode} className="h-3.5 w-5 rounded-[2px] border border-[#151827]/25" />
          <span>{currentShort}</span>
          <span className="text-[10px] text-[#151827]/70" aria-hidden="true">▾</span>
        </button>

        {isOpen && (
          <div
            role="listbox"
            aria-label={t('common.language')}
            className="absolute right-0 top-[calc(100%+8px)] min-w-full overflow-hidden rounded-2xl border-2 border-[#151827] bg-white shadow-[6px_6px_0_#151827]"
          >
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isActive = lang === language;
              const longLabel = NATIVE_LABEL[lang] || lang.toUpperCase();
              const flagCode = FLAG_CODE[lang] || 'gb';
              return (
                <button
                  key={lang}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    if (!isActive) {
                      handleChange({ target: { value: lang } });
                    }
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm font-black uppercase tracking-[0.08em] transition ${isActive ? 'bg-[#151827] text-white' : 'bg-white text-[#151827] hover:bg-[#fff4df]'}`}
                >
                  <span className="flex items-center gap-2">
                    <FlagIcon code={flagCode} className="h-3.5 w-5 rounded-[2px] border border-current/25" />
                    <span className={`normal-case tracking-normal ${isActive ? 'text-white' : 'text-[#151827]'}`}>
                      {longLabel}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'labeled') {
    return (
      <label className={`inline-flex items-center gap-2 text-sm text-slate-600 ${className}`}>
        <span>{t('common.language')}:</span>
        {renderNativeSelectShell()}
      </label>
    );
  }

  return <div className={className}>{renderNativeSelectShell()}</div>;
}

export default LanguageSwitcher;
